import {Logger} from 'neo-common';
import {Balance, Cashier, FeatureData, GameDataLoaded, Help, IPlatforms, Paytable, RoundEnded, RoundInit, RoundStarted, SpinReceived} from './IPlatforms';
import {Platforms} from './Platforms';

export class PlatformSynotAPI implements IPlatforms {

    private isGameRoundFinished: boolean;

    private gamePlayDisabled: boolean;

    private logger: boolean;

    private progress: number;

    constructor() {
        this.isGameRoundFinished = true;
        this.gamePlayDisabled = false;
        this.logger = false;
    }

    public create(): void {
        Platforms.canPlay = true;

        window.addEventListener('message', this.receiveMessage.bind(this));
    }

    /* GAME TO PLATFORM */

    public loadProgress(progress: number): void {
        if (this.progress === 100) {
            return;
        }

        this.sendMessage('g2p.loadProgress', {progress: progress});

        this.progress = progress;
    }

    public gameReady(): void {
        this.sendMessage('g2p.gameReady');
    }

    public gameFailed(): void {
        this.sendMessage('g2p.gameFailed');
    }

    public gameClosing(): void {
        this.sendMessage('g2p.gameClosing');
    }

    public gameReloading(): void {
        this.sendMessage('g2p.gameReloading');
    }

    public gameChange(gameCode: string): void {
        this.sendMessage('g2p.gameChange', {gameCode: gameCode});
    }

    public error(code: string, message: string): void {
        this.sendMessage('g2p.error', {code: code, message: message});
    }

    public clientError(id: string, message: string): void {}

    public roundInit(args?: RoundInit): void {
        this.sendMessage('g2p.roundInit', args);
    }

    public roundStarted(args?: RoundStarted): void {
        this.isGameRoundFinished = false;
        this.sendMessage('g2p.roundStarted', args);
    }

    public spinStarted(): void {
        this.sendMessage('g2p.spinStarted');
    }

    public spinReceived(args?: SpinReceived): void {
        this.sendMessage('g2p.spinReceived', args);
    }

    public spinEnded(): void {
        this.sendMessage('g2p.spinEnded');
    }

    public roundEnded(args?: RoundEnded): void {
        const data = {};
        if (args != null) {
            if (args.id != null) {
                data['roundId'] = args.id;
            }

            if (args.win != null) {
                data['win'] = args.win;
            }

            if (args.balanceAfter != null) {
                data['balance'] = args.balanceAfter;
            }
        }

        this.isGameRoundFinished = true;
        this.sendMessage('g2p.roundEnded', data);
    }

    public featureStarted(args?: FeatureData): void {
        this.sendMessage('g2p.featureStarted', args);
    }

    public featureInit(args?: FeatureData): void {
        this.sendMessage('g2p.featureInit', args);
    }

    public featureFinished(args?: FeatureData): void {
        this.sendMessage('g2p.featureFinished', args);
    }

    public autoplayStarted(): void {
        this.sendMessage('g2p.autoplayStarted');
    }

    public autoplayFinished(): void {
        this.sendMessage('g2p.autoplayFinished');
    }

    public gotoCasier(args?: Cashier): void {
        this.sendMessage('g2p.goToCashier', args);
    }

    public gotoHistory(args?: History): void {
        this.sendMessage('g2p.goToHistory', args);
    }

    public gotoHelp(args?: Help): void {
        this.sendMessage('g2p.goToHelp', args);
    }

    public gotoPaytable(args?: Paytable): void {
        this.sendMessage('g2p.goToPaytable', args);
    }

    public balanceUpdated(args?: Balance): void {
        const data = {};
        if (args != null) {
            if (args.amount != null) {
                data['balance'] = args.amount;
            }
        }

        this.sendMessage('g2p.balanceUpdated', data);
    }

    public setVolume(level: number): void {
        this.sendMessage('g2p.setVolume', {level: level});
    }

    public quickSpinEnabled(enabled: boolean): void {
        this.sendMessage('g2p.quickSpin', {enabled: enabled});
    }

    public bet(value: number): void {
        this.sendMessage('g2p.betChanged', {bet: value});
    }

    public win(value: number, cumulative?: boolean): void {
        this.sendMessage('g2p.win', {win: value, cumulative: cumulative});
    }

    public gameDataLoaded(args: GameDataLoaded): void {
        const data = {};
        if (args != null) {
            if (args.PlayerTokenId != null) {
                data['playerTokenId'] = args.PlayerTokenId;
            }

            if (args.GameName != null) {
                data['gameName'] = args.GameName;
            }

            if (args.HomeUrl != null) {
                data['homeUrl'] = args.HomeUrl;
            }

            if (args.ClientToken != null) {
                data['clientToken'] = args.ClientToken;
            }

            if (args.SessionData != null) {
                data['sessionData'] = {};

                if (args.SessionData.SessionBalance) {
                    data['sessionData']['balance'] = args.SessionData.SessionBalance;
                }

                if (args.SessionData.TotalWin) {
                    data['sessionData']['totalWin'] = args.SessionData.TotalWin;
                }

                if (args.SessionData.TotalBet) {
                    data['sessionData']['totalBet'] = args.SessionData.TotalBet;
                }
            }

            if (args.CurrencyCode != null) {
                data['currencyCode'] = args.CurrencyCode;
            }

            if (args.Balance != null) {
                data['balance'] = args.Balance;
            }

            if (args.Bet != null) {
                data['bet'] = args.Bet;
            }
        }

        this.sendMessage('g2p.gameDataLoaded', data);
    }

    public dialogOpened(type: string, isOpen: boolean): void {}

    public fullscreen(enabled: boolean): void {
        const data = {'enabled': enabled};

        this.sendMessage('g2p.fullscreen', data);
    }

    public sendPluginMessage(message: string): void {}

    public userAction(action: string, data: Object): void {}

    private sendMessage(event: string, data?: any): void {
        if (Platforms.pluginOrigin == null) {
            if (DEBUG) {
                this.log('Error - missing plugin origin parameter!');
            }

            return;
        }

        const message = {event: event};
        if (data != null) {
            message['data'] = data;
        }

        if (this.logger) {
            console.log(`Synot Platform API - Sending event: ${event} ${data ? JSON.stringify(data) : ''}`);
        }

        if (parent != null) {
            parent.postMessage(message, Platforms.pluginOrigin);
        }
    }

    /* PLATFORM TO GAME */

    //ToDo add settings - for example - handleShowErrorDialog
    private receiveMessage(event: any): void {
        if (DEBUG) {
            this.log('receiveMessage');
        }

        if (event != null && event.data != null) {
            if (!this.checkOrigin(event.origin)) {
                if (DEBUG) {
                    this.log('Received message with incorrect origin.');
                }
                return;
            }

            if (event.data.event != null) {
                if (this.logger) {
                    console.log(`Synot Platform API - Received event: ${event.data.event} ${event.data.data ? JSON.stringify(event.data.data) : ''}`);
                }

                switch (event.data.event) {
                    case 'p2g.stopAutoplay':
                        Platforms.stopAutoplay();
                        break;
                    case 'p2g.refreshBalance':
                        Platforms.refreshBalance();
                        break;
                    case 'p2g.gamePlayDisable':
                        this.gamePlayDisabled = true;
                        Platforms.gamePlayStop(this.isGameRoundFinished);
                        break;
                    case 'p2g.gamePlayResume':
                        if (this.gamePlayDisabled && this.isGameRoundFinished) {
                            this.gamePlayDisabled = false;
                            Platforms.gamePlayResume();
                        }
                        break;
                    case 'p2g.gamePlayFreeze':
                        Platforms.gamePlayFreeze();
                        break;
                    case 'p2g.gamePlayUnfreeze':
                        Platforms.gamePlayUnfreeze();
                        break;
                    case 'p2g.gameAudioEnable':
                        Platforms.gameAudioEnable();
                        break;
                    case 'p2g.gameAudioMute':
                        Platforms.gameAudioMute();
                        break;
                    case 'p2g.gameEnableQuickSpin':
                        Platforms.gameQuickSpin(true);
                        break;
                    case 'p2g.gameDisableQuickSpin':
                        Platforms.gameQuickSpin(false);
                        break;
                    case 'p2g.showHelp':
                        Platforms.showHelp();
                        break;
                    case 'p2g.hideHelp':
                        Platforms.hideHelp();
                        break;
                    case 'p2g.toggleHelp':
                        Platforms.toggleHelp();
                        break;
                    case 'p2g.showPaytable':
                        Platforms.showPaytable();
                        break;
                    case 'p2g.hidePaytable':
                        Platforms.hidePaytable();
                        break;
                    case 'p2g.togglePaytable':
                        Platforms.togglePaytable();
                        break;
                    case 'p2g.configure':
                        this.configure(event.data.data);
                        break;
                }
            }
        }
    }

    private configure(data: any): void {
        if (data == null) {
            return;
        }

        if (data['handleErrorDialog'] != null) {
            Platforms.handleShowErrorDialog = data['handleErrorDialog'];
        }

        if (data['logger'] != null) {
            this.logger = data['logger'];
        }
    }

    private checkOrigin(origin: string): boolean {
        if (Platforms.pluginOrigin !== '*' && origin !== Platforms.pluginOrigin) {
            console.log(`Synot Platform API - Wrong origin - expected: ${Platforms.pluginOrigin} get: ${origin}`);
            return false;
        }

        return true;
    }

    /* LOGGER */

    protected log(s: string): void {
        if (DEBUG) {
            Logger.debugGroup('PlatformSynotAPI', 'PlatformSynotAPI' + s);
        }
    }
}


/** WEBPACK FOOTER **
 ** ./Engine/Platforms/PlatformSynotAPI.ts
 **/